import { useMemo } from "react";
import { View } from "@common/types/views/views";
import { EntityProfileIndustry, EntityProfileType } from "@common/types/entity";
import { FLAT_RATE_PLANS, flatRatePlanNames } from "@common/flatRateBilling";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import {
    entityProfileTypeLabels,
    industryLabels,
} from "@components/entity/constants";
import { applyCloseTemplateEntitySelectionViewConfig } from "./applyCloseTemplateEntitySelectionViewConfig";
import { defaultApplyCloseEntitySelectionView } from "./applyCloseTemplateEntitySelectionView.constants";
import {
    ALL_INDUSTRIES,
    ALL_LEGAL_TYPES,
    ALL_PLANS,
} from "./applyCloseTemplateEntityFilters";
import { MultiSelectFilterCombobox } from "./MultiSelectFilterCombobox";

interface Props {
    search: string;
    onSearchChange: (val: string) => void;
    selectedPlans: FLAT_RATE_PLANS[];
    onPlansChange: (values: FLAT_RATE_PLANS[]) => void;
    selectedIndustries: EntityProfileIndustry[];
    onIndustriesChange: (values: EntityProfileIndustry[]) => void;
    selectedLegalTypes: EntityProfileType[];
    onLegalTypesChange: (values: EntityProfileType[]) => void;
    view: View;
    onViewChange: (view: View) => void;
    /** Column catalog for the settings dropdown. Omit columns that cannot be shown (e.g. dates on the schedule variant). */
    viewConfig: typeof applyCloseTemplateEntitySelectionViewConfig;
    searchPlaceholder: string;
    /** Whether to render the client-level Plans filter (hidden for single-client templates). */
    showPlansFilter: boolean;
}

function viewWithConfigColumns(
    view: View,
    viewConfig: typeof applyCloseTemplateEntitySelectionViewConfig,
): View {
    const allowedColumnIds = new Set(Object.keys(viewConfig.columns));
    return {
        ...view,
        columns: view.columns.filter((col) => allowedColumnIds.has(col.id)),
    };
}

export const ApplyCloseTemplateEntityFilterBar = ({
    search,
    onSearchChange,
    selectedPlans,
    onPlansChange,
    selectedIndustries,
    onIndustriesChange,
    selectedLegalTypes,
    onLegalTypesChange,
    view,
    onViewChange,
    viewConfig,
    searchPlaceholder,
    showPlansFilter,
}: Props) => {
    const columnSettingsView = useMemo(
        () => viewWithConfigColumns(view, viewConfig),
        [view, viewConfig],
    );
    const columnSettingsDefaultView = useMemo(
        () =>
            viewWithConfigColumns(
                defaultApplyCloseEntitySelectionView,
                viewConfig,
            ),
        [viewConfig],
    );

    return (
        <TablePageHeader
            search={search}
            onSearchChange={onSearchChange}
            searchPlaceholder={searchPlaceholder}
            searchKeyboardShortcuts={false}
            isFullPage
        >
            {showPlansFilter && (
                <MultiSelectFilterCombobox
                    items={ALL_PLANS}
                    selectedValues={selectedPlans}
                    onValuesChange={onPlansChange}
                    getLabel={(plan) => flatRatePlanNames[plan] ?? plan}
                    placeholder="All plans"
                    emptyMessage="No matching plans"
                    selectedNoun="plan"
                />
            )}

            <MultiSelectFilterCombobox
                items={ALL_INDUSTRIES}
                selectedValues={selectedIndustries}
                onValuesChange={onIndustriesChange}
                getLabel={(ind) => industryLabels[ind] ?? ind}
                placeholder="All industries"
                emptyMessage="No matching industries"
                searchPlaceholder="Search industries..."
                selectedNoun="industry"
            />

            <MultiSelectFilterCombobox
                items={ALL_LEGAL_TYPES}
                selectedValues={selectedLegalTypes}
                onValuesChange={onLegalTypesChange}
                getLabel={(lt) => entityProfileTypeLabels[lt] ?? lt}
                placeholder="All legal types"
                emptyMessage="No matching legal types"
                searchPlaceholder="Search legal types..."
                selectedNoun="legal type"
            />

            <ColumnSettings
                view={columnSettingsView}
                viewConfig={viewConfig}
                defaultView={columnSettingsDefaultView}
                onViewChange={onViewChange}
            />
        </TablePageHeader>
    );
};
